import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { prefersReducedMotion } from '../../../../utils/reducedMotion';

// Monochrome star palette (user ruling, device pass 1k — the color ribbons
// read kid's-birthday; B&W stars keep the beat inside the app's language).
const CONFETTI_COLORS = ['#000000', '#3A3A3C', '#8E8E93', '#C7C7CC'];
const PARTICLE_COUNT = 160;
const DURATION_MS = 1600;
// The burst mounts on the Connect tap, while the iOS keyboard + sheet are
// still animating out and fixed layers are mis-anchored — a burst that runs
// through that window plays to nobody (device pass 1k: "no stars", twice).
// Wait for the viewport to settle, THEN size the canvas and fire.
const SETTLE_MS = 400;

// 5-point star path centered on the origin, outer radius r
const drawStar = (ctx, r) => {
  const inner = r * 0.45;
  ctx.beginPath();
  for (let i = 0; i < 10; i += 1) {
    const radius = i % 2 === 0 ? r : inner;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    ctx.lineTo(Math.cos(a) * radius, Math.sin(a) * radius);
  }
  ctx.closePath();
  ctx.fill();
};

/**
 * ConfettiBurst — the TEXT share's completion beat (device pass 1 ruling,
 * 2026-08-13: the share landed flat — celebration at COMPLETION is earned,
 * the anti-machine rule guards the ask, not the payoff). A ~1.4s hand-rolled
 * canvas burst — no dependency, pointer-transparent, self-removing via
 * onDone. Honors prefers-reduced-motion (skips the particles, quick onDone).
 *
 * Feature component, deliberately not a UISystem atom — single consumer;
 * promote with a design pass if a second celebration appears.
 */
const ConfettiBurst = ({ origin, onDone }) => {
  const originRef = useRef(origin);
  originRef.current = origin;
  const canvasRef = useRef(null);
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    if (prefersReducedMotion()) {
      const t = setTimeout(() => onDoneRef.current?.(), 300);
      return () => clearTimeout(t);
    }

    let raf;
    const settleTimer = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      // Sized AFTER the settle, from the layout viewport — with the
      // keyboard gone these now agree with what's on screen
      const w = document.documentElement.clientWidth;
      const h = document.documentElement.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);

      // Tiny stars radiating from the TAP POINT — the person's Connect tap
      // is what makes it happen, so the sparkle answers their finger. A
      // point outside the settled viewport (keyboard-era coordinates gone
      // wrong) falls back to mid-lower center rather than firing off-screen.
      let cx = originRef.current?.x;
      let cy = originRef.current?.y;
      if (!(cx >= 0 && cx <= w)) cx = w / 2;
      if (!(cy >= 0 && cy <= h)) cy = h * 0.6;
      const particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
        const angle = Math.random() * Math.PI * 2;
        const speed = 3 + Math.random() * 8;
        return {
          x: cx,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 2 + Math.random() * 3,
          color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.2,
        };
      });

      const start = performance.now();
      const tick = (now) => {
        const t = now - start;
        ctx.clearRect(0, 0, w, h);
        const fade = Math.max(0, 1 - t / DURATION_MS);
        particles.forEach((p) => {
          p.x += p.vx;
          p.y += p.vy;
          // Drift outward and settle — no gravity rain, the burst just
          // disperses and fades in place
          p.vx *= 0.97;
          p.vy *= 0.97;
          p.rot += p.vr;
          ctx.save();
          ctx.globalAlpha = fade;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          drawStar(ctx, p.size);
          ctx.restore();
        });
        if (t < DURATION_MS) {
          raf = requestAnimationFrame(tick);
        } else {
          onDoneRef.current?.();
        }
      };
      raf = requestAnimationFrame(tick);
    }, SETTLE_MS);

    return () => {
      clearTimeout(settleTimer);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        // Canvas is a REPLACED element: inset alone doesn't stretch it — it
        // renders at its intrinsic (attribute) size, i.e. dpr× the screen
        // (dev-DB telemetry 2026-08-15: canvasRect 1170x2088 on a 390x696
        // viewport — the burst drew below the visible area every round).
        // Explicit CSS size pins the element; the browser scales the
        // dpr-sized buffer into it.
        width: '100%',
        height: '100%',
        zIndex: 3000,
        pointerEvents: 'none',
      }}
    />
  );
};

ConfettiBurst.propTypes = {
  // Viewport point the burst emanates from (the Connect tap); null/absent
  // falls back to lower-center
  origin: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  onDone: PropTypes.func,
};

export default ConfettiBurst;
